// ============================
// File: src/features/poc/CheckpointBoard.jsx
// ============================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { EvidenceUploader } from "./components/EvidenceUploader";
import { useMutation, useQuery, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getPocSummary, savePocSetup, listEvidence, createReminderRules, putEvidenceMetadata, generatePocReport } from "./api/pocClient";

const fmt = (d) => new Date(d).toLocaleDateString();
const dayms = 24*60*60*1000;

function useMilestones(startDate) {
  return useMemo(() => {
    const start = startDate ? new Date(startDate).getTime() : Date.now();
    const d30 = new Date(start + 30*dayms);
    const d60 = new Date(start + 60*dayms);
    const d90 = new Date(start + 90*dayms);
    return { d30, d60, d90 };
  }, [startDate]);
}

function StatusDot({ due, completedAt }) {
  const now = Date.now();
  const dueMs = new Date(due).getTime();
  const isDue = now >= dueMs;
  const late = isDue && !completedAt;
  const color = completedAt ? "bg-green-500" : late ? "bg-amber-500 animate-pulse" : "bg-gray-300";
  const title = completedAt ? `Completed ${fmt(completedAt)}` : late ? "Due" : `Due ${fmt(due)}`;
  return <span title={title} className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} />;
}

function Tile({ title, due, checkpointKey, value, onChange, evidence, onOpenEvidence, completedAt }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <StatusDot due={due} completedAt={completedAt} />
          {title}
        </div>
        <div className="text-xs text-gray-500">Due {fmt(due)}</div>
      </div>
      <textarea
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        placeholder="Notes, status summary, risks surfaced, metrics observed…"
        rows={4}
        value={value || ""}
        onChange={(e) => onChange(checkpointKey, e.target.value)}
      />
      <div className="flex items-center justify-between">
        <button onClick={() => onOpenEvidence(checkpointKey)} className="text-sm rounded-md border px-3 py-1 hover:bg-gray-50">Upload/View evidence</button>
        {completedAt ? (
          <div className="text-xs text-green-700">Completed {fmt(completedAt)}</div>
        ) : (
          <div className="text-xs text-gray-500">Attach evidence and save to complete</div>
        )}
      </div>
      {!!evidence?.length && (
        <ul className="text-xs text-gray-700 list-disc ml-5">
          {evidence.map((f) => (
            <li key={f.key}><a className="text-blue-600 hover:underline" href={f.url} target="_blank" rel="noreferrer">{f.name}</a></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SetupForm({ value, onChange, onSave, saving }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="text-lg font-semibold text-gray-900 mb-2">POC Setup</div>
      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-gray-600">POC Title</span>
          <input className="mt-1 w-full rounded-md border px-3 py-2" value={v.title||""} onChange={(e)=>set("title", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Start Date</span>
          <input type="date" className="mt-1 w-full rounded-md border px-3 py-2" value={v.startDate||""} onChange={(e)=>set("startDate", e.target.value)} />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs text-gray-600">Scope</span>
          <textarea className="mt-1 w-full rounded-md border px-3 py-2" rows={2} value={v.scope||""} onChange={(e)=>set("scope", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Hypotheses</span>
          <textarea className="mt-1 w-full rounded-md border px-3 py-2" rows={2} value={v.hypotheses||""} onChange={(e)=>set("hypotheses", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Success metrics (quant & qual)</span>
          <textarea className="mt-1 w-full rounded-md border px-3 py-2" rows={2} value={v.metrics||""} onChange={(e)=>set("metrics", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Environment plan</span>
          <textarea className="mt-1 w-full rounded-md border px-3 py-2" rows={2} value={v.envPlan||""} onChange={(e)=>set("envPlan", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Data plan</span>
          <textarea className="mt-1 w-full rounded-md border px-3 py-2" rows={2} value={v.dataPlan||""} onChange={(e)=>set("dataPlan", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Estimated cost ($)</span>
          <input type="number" className="mt-1 w-full rounded-md border px-3 py-2" value={v.estimatedCost||""} onChange={(e)=>set("estimatedCost", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Team roster / Vendors</span>
          <textarea className="mt-1 w-full rounded-md border px-3 py-2" rows={2} value={v.team||""} onChange={(e)=>set("team", e.target.value)} />
        </label>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={onSave} disabled={saving} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60">{saving?"Saving…":"Save Setup"}</button>
      </div>
    </div>
  );
}

function BoardInner({ demandId }) {
  // Load initial summary (includes setup & previous checkpoints)
  const { data, refetch, error: loadError } = useQuery({
    queryKey: ["poc", demandId],
    queryFn: () => getPocSummary(demandId),
    staleTime: 10_000,
    enabled: Boolean(demandId),
    retry: false,
  });
  const [setup, setSetup] = useState({ startDate: new Date().toISOString().slice(0,10) });
  const [notes, setNotes] = useState({ d30: "", d60: "", d90: "" });
  const [openEvidenceFor, setOpenEvidenceFor] = useState(null);

  useEffect(() => {
    if (!data) return;
    // Keep the seeded start date when the server has no setup saved yet — replacing
    // it with {} made all three milestone dates collapse onto today.
    setSetup((prev) => (data.setup && Object.keys(data.setup).length ? data.setup : prev));
    setNotes(data.notes || { d30: "", d60: "", d90: "" });
  }, [data]);

  // Checkpoint notes are saved with the setup. They previously lived only in local
  // state — savePocSetup sent `setup` alone — so everything typed into the three
  // checkpoint tiles was silently discarded on reload.
  const saveSetupMut = useMutation({
    mutationFn: (payload) => savePocSetup(demandId, payload, notes),
    onSuccess: () => refetch(),
  });
  const remindersMut = useMutation({ mutationFn: () => createReminderRules(demandId, setup.startDate), onSuccess: () => refetch() });
  const reportMut = useMutation({ mutationFn: () => generatePocReport(demandId), onSuccess: () => refetch() });

  const { d30, d60, d90 } = useMilestones(setup.startDate);

  const { data: evidence30 } = useQuery({ queryKey: ["files", demandId, "d30"], queryFn: () => listEvidence(demandId, "d30"), enabled: !!demandId });
  const { data: evidence60 } = useQuery({ queryKey: ["files", demandId, "d60"], queryFn: () => listEvidence(demandId, "d60"), enabled: !!demandId });
  const { data: evidence90 } = useQuery({ queryKey: ["files", demandId, "d90"], queryFn: () => listEvidence(demandId, "d90"), enabled: !!demandId });

  const onTileChange = (k, val) => setNotes((prev) => ({ ...prev, [k]: val }));

  const onEvidenceUploaded = async (checkpointKey, fileMeta) => {
    await putEvidenceMetadata(demandId, checkpointKey, fileMeta);
    await refetch();
  };

  const mutationError = saveSetupMut.error || remindersMut.error || reportMut.error;

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-6">
      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          Could not load POC data for {demandId}: {loadError.message}
        </div>
      )}
      {mutationError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          {mutationError.message}
        </div>
      )}
      {reportMut.data && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          <div className="font-medium">POC report generated — outcome: {reportMut.data.outcome}</div>
          <div className="mt-1 whitespace-pre-wrap">{reportMut.data.findings}</div>
        </div>
      )}
      {remindersMut.data && remindersMut.data.scheduled === false && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {remindersMut.data.message}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <SetupForm value={setup} onChange={setSetup} onSave={() => saveSetupMut.mutate(setup)} saving={saveSetupMut.isLoading} />
          <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-gray-700">Reminders</div>
            <p className="text-xs text-gray-500 mb-2">Auto-create 30/60/90 reminder rules (EventBridge/Lambda via backend)</p>
            <button onClick={() => remindersMut.mutate()} disabled={remindersMut.isLoading} className="rounded-md border px-3 py-2 text-sm hover:bg-gray-50">
              {remindersMut.isLoading ? "Setting up…" : "Enable reminders"}
            </button>
          </div>
        </div>
        <div className="lg:col-span-2 grid md:grid-cols-3 gap-4">
          <Tile title="30-Day Checkpoint" due={d30} checkpointKey="d30" value={notes.d30} onChange={onTileChange} evidence={evidence30||[]} onOpenEvidence={setOpenEvidenceFor} completedAt={data?.completed?.d30} />
          <Tile title="60-Day Checkpoint" due={d60} checkpointKey="d60" value={notes.d60} onChange={onTileChange} evidence={evidence60||[]} onOpenEvidence={setOpenEvidenceFor} completedAt={data?.completed?.d60} />
          <Tile title="90-Day Checkpoint" due={d90} checkpointKey="d90" value={notes.d90} onChange={onTileChange} evidence={evidence90||[]} onOpenEvidence={setOpenEvidenceFor} completedAt={data?.completed?.d90} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">Generate the POC report once evidence and notes are complete.</div>
        <button onClick={() => reportMut.mutate()} disabled={reportMut.isLoading} className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
          {reportMut.isLoading ? "Generating…" : "Generate Report"}
        </button>
      </div>

      {openEvidenceFor && (
        <EvidenceUploader
          demandId={demandId}
          checkpointKey={openEvidenceFor}
          onClose={() => setOpenEvidenceFor(null)}
          onUploaded={(meta) => onEvidenceUploaded(openEvidenceFor, meta)}
        />
      )}
    </div>
  );
}

export default function CheckpointBoard({ demandId }) {
  const client = useMemo(() => new QueryClient(), []);
  return (
    <QueryClientProvider client={client}>
      <BoardInner demandId={demandId} />
    </QueryClientProvider>
  );
}





